import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { Loader2, ShieldX } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

import { Input } from '@/components/ui/input'
import { apiRequest } from '@/lib/api-client'
import { useWorkspace } from '@/features/workspace/workspace-provider'
import { KNOWN_CHAT_MODELS, MODEL_REGISTRY, listSectorPacks } from '@mindkeep/shared'

const auditEntrySchema = z.object({
  id: z.string(),
  actor_user_id: z.string().nullable().optional(),
  user_id: z.string().nullable().optional(),
  action: z.string(),
  resource_type: z.string().nullable(),
  resource_id: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable().optional(),
  details: z.record(z.unknown()).nullable().optional(),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  created_at: z.string(),
})

const auditResponseSchema = z.object({
  entries: z.array(auditEntrySchema),
})

const chatModelsResponseSchema = z.object({
  models: z.array(z.string()),
  defaults: z.array(z.string()),
})

const providerPolicySchema = z.object({
  eu_only: z.boolean(),
  allowed_providers: z.array(z.string()),
  pii_policy: z.enum(['block', 'warn', 'off']),
  research_providers: z.enum(['all', 'off']),
})

const providerPolicyResponseSchema = z.object({
  provider_policy: providerPolicySchema,
  defaults: z.object({ provider_policy: providerPolicySchema }).optional(),
})

const sectorPackResponseSchema = z.object({
  sector_pack: z.string().nullable(),
  pack: z.object({ id: z.string(), label: z.string(), version: z.string() }).nullable(),
})

const defaultModelResponseSchema = z.object({
  default_model: z.string(),
})

type AuditEntry = z.infer<typeof auditEntrySchema>
type ProviderPolicy = z.infer<typeof providerPolicySchema>

export function AdminPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [actionFilter, setActionFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isAdmin, roleLoading, roleError } = useWorkspace()
  const qc = useQueryClient()

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: '100' })
    if (actionFilter.trim()) params.set('action', actionFilter.trim())
    return params.toString()
  }, [actionFilter])

  async function loadAudit() {
    setLoading(true)
    setError(null)
    try {
      const data = await apiRequest({
        path: `/admin/audit?${query}`,
        schema: auditResponseSchema,
      })
      setEntries(data.entries)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Audit-Log konnte nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Kein Fetch, bevor die Rolle bekannt ist — Nicht-Admins laden nichts.
    if (!isAdmin) return
    void loadAudit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  // M5-B: Rolle kommt aus dem WorkspaceProvider (/workspaces/me). Waehrend des
  // Ladens warten, danach fail-closed: nur owner/admin sehen die Admin-Flaeche.
  // Die eigentliche Durchsetzung bleibt serverseitig (403 auf /api/admin/*).
  if (roleLoading) {
    return (
      <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Berechtigungen werden geprüft…
          </CardContent>
        </Card>
      </div>
    )
  }

  if (roleError && !isAdmin) {
    // /workspaces/me ist fehlgeschlagen: echten Admins nicht irrtuemlich
    // "Kein Zugriff" zeigen, sondern Fehler mit Wiederholung anbieten.
    return (
      <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldX className="h-5 w-5 text-muted-foreground" />
              Berechtigungen konnten nicht geprüft werden
            </CardTitle>
            <CardDescription>
              Die Verbindung zum Server ist fehlgeschlagen. Bitte erneut versuchen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void qc.invalidateQueries({ queryKey: ['workspace', 'current'] })}
            >
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldX className="h-5 w-5 text-muted-foreground" />
              Kein Zugriff
            </CardTitle>
            <CardDescription>
              Diese Admin-Ansicht ist nur für Workspace-Owner und Admins verfügbar.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
      <header>
        <h1 className="text-[26px] font-[650] tracking-tight">Admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sensible Workspace-Aktionen und Security-Ereignisse.
        </p>
      </header>

      <ProviderPolicyCard />

      <SectorPackCard />

      <ChatModelsCard />

      <Card>
        <CardHeader>
          <CardTitle>Audit-Log</CardTitle>
          <CardDescription>Nur Workspace-Owner und Admins können diese Ansicht öffnen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={actionFilter}
              onChange={(event) => setActionFilter(event.target.value)}
              placeholder="Aktion filtern, z. B. vault oder brand"
              className="max-w-xs"
            />
            <Button type="button" onClick={() => void loadAudit()} disabled={loading}>
              {loading ? 'Lädt…' : 'Aktualisieren'}
            </Button>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Zeit</th>
                  <th className="px-3 py-2 font-medium">Aktion</th>
                  <th className="px-3 py-2 font-medium">Ressource</th>
                  <th className="px-3 py-2 font-medium">Akteur</th>
                  <th className="px-3 py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-t">
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {new Date(entry.created_at).toLocaleString('de-CH')}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline">{entry.action}</Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {entry.resource_type ?? '—'}
                      {entry.resource_id ? `:${entry.resource_id.slice(0, 8)}` : ''}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {(entry.actor_user_id ?? entry.user_id ?? '—').slice(0, 12)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {entry.ip_address ?? '—'}
                    </td>
                  </tr>
                ))}
                {!entries.length && !loading ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                      Keine Audit-Einträge gefunden.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const PROVIDER_LABELS: Record<string, string> = {
  mistral: 'Mistral',
  moonshot: 'Moonshot',
  anthropic: 'Anthropic',
  deepseek: 'DeepSeek',
  google: 'Google',
  openai: 'OpenAI',
}

interface ProviderInfo {
  id: string
  label: string
  region: 'eu' | 'non-eu'
}

// Provider-Liste aus dem zentralen MODEL_REGISTRY (EDIH §2.1): ein Provider
// gilt nur dann als EU, wenn alle seine registrierten Modelle EU-Region haben.
const PROVIDERS: ProviderInfo[] = (() => {
  const byProvider = new Map<string, ProviderInfo>()
  for (const entry of MODEL_REGISTRY) {
    const existing = byProvider.get(entry.provider)
    if (existing) {
      if (entry.region !== 'eu') existing.region = 'non-eu'
    } else {
      byProvider.set(entry.provider, {
        id: entry.provider,
        label: PROVIDER_LABELS[entry.provider] ?? entry.provider,
        region: entry.region,
      })
    }
  }
  return [...byProvider.values()]
})()

function ProviderPolicyCard() {
  const [policy, setPolicy] = useState<ProviderPolicy | null>(null)
  const [defaults, setDefaults] = useState<ProviderPolicy | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await apiRequest({
        path: '/admin/provider-policy',
        schema: providerPolicyResponseSchema,
      })
      setPolicy(data.provider_policy)
      setDefaults(data.defaults?.provider_policy ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provider-Policy konnte nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const save = async () => {
    if (!policy) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const data = await apiRequest({
        path: '/admin/provider-policy',
        method: 'PUT',
        body: { provider_policy: policy },
        schema: providerPolicyResponseSchema,
      })
      setPolicy(data.provider_policy)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provider-Policy konnte nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  const resetToDefaults = () => {
    if (defaults) setPolicy(defaults)
    setSaved(false)
  }

  const toggleProvider = (providerId: string) => {
    setPolicy((prev) => {
      if (!prev) return prev
      const allowed = prev.allowed_providers.includes(providerId)
        ? prev.allowed_providers.filter((p) => p !== providerId)
        : [...prev.allowed_providers, providerId]
      return { ...prev, allowed_providers: allowed }
    })
    setSaved(false)
  }

  return (
    <Card data-testid="provider-policy-card">
      <CardHeader>
        <CardTitle>Provider-Policy</CardTitle>
        <CardDescription>
          Steuert, welche KI-Provider dieser Workspace nutzen darf. Nicht-EU-Provider übertragen
          Daten ausserhalb der EU.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading || !policy ? (
          error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Lädt…</p>
          )
        ) : (
          <>
            <label className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-muted">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={policy.eu_only}
                onChange={(event) => {
                  setPolicy({ ...policy, eu_only: event.target.checked })
                  setSaved(false)
                }}
              />
              <span className="text-sm font-medium">Nur EU-Modelle</span>
              <span className="ml-auto text-xs text-muted-foreground">
                Blockiert sämtliche Nicht-EU-Modelle, unabhängig von der Provider-Auswahl
              </span>
            </label>

            <div className="space-y-2">
              {PROVIDERS.map((provider) => (
                <label
                  key={provider.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={policy.allowed_providers.includes(provider.id)}
                    onChange={() => toggleProvider(provider.id)}
                  />
                  <span className="text-sm font-medium">{provider.label}</span>
                  <span className="ml-auto">
                    {provider.region === 'eu' ? (
                      <Badge variant="outline">EU</Badge>
                    ) : (
                      <Badge variant="secondary">Nicht-EU</Badge>
                    )}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label htmlFor="pii-policy" className="text-sm font-medium">
                PII-Behandlung
              </label>
              <select
                id="pii-policy"
                value={policy.pii_policy}
                onChange={(event) => {
                  setPolicy({ ...policy, pii_policy: event.target.value as ProviderPolicy['pii_policy'] })
                  setSaved(false)
                }}
                className="h-11 min-[761px]:h-9 rounded-md border border-input bg-background px-2 text-[16px] min-[761px]:text-sm"
              >
                <option value="block">PII blockieren</option>
                <option value="warn">PII warnen</option>
                <option value="off">PII aus</option>
              </select>
            </div>

            <label className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-muted">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={policy.research_providers === 'all'}
                onChange={(event) => {
                  setPolicy({
                    ...policy,
                    research_providers: event.target.checked ? 'all' : 'off',
                  })
                  setSaved(false)
                }}
              />
              <span className="text-sm font-medium">Externe Recherche-Quellen</span>
              <span className="ml-auto text-xs text-muted-foreground">
                Web-Recherche über externe Such-Provider erlauben
              </span>
            </label>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {saved ? <p className="text-sm text-success" role="status">Gespeichert.</p> : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={() => void save()} disabled={saving || policy.allowed_providers.length === 0}>
                {saving ? 'Speichert…' : 'Speichern'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetToDefaults}
                disabled={saving || !defaults}
              >
                Auf Standard zurücksetzen
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

const SECTOR_PACKS = listSectorPacks()

function SectorPackCard() {
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const activePackId = workspace.sector_pack?.id ?? ''
  const activePack = SECTOR_PACKS.find((pack) => pack.id === activePackId) ?? null

  const changePack = async (packId: string) => {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await apiRequest({
        path: '/admin/sector-pack',
        method: 'PUT',
        body: { sector_pack: packId === '' ? null : packId },
        schema: sectorPackResponseSchema,
      })
      await qc.invalidateQueries({ queryKey: ['workspace', 'current'] })
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Branchenpaket konnte nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card data-testid="sector-pack-card">
      <CardHeader>
        <CardTitle>Branchenpaket</CardTitle>
        <CardDescription>
          Aktiviert branchenspezifische Terminologie, Freigabe-Regeln und Disclaimer für diesen
          Workspace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="sector-pack" className="text-sm font-medium">
            Aktives Paket
          </label>
          <select
            id="sector-pack"
            value={activePackId}
            disabled={saving}
            onChange={(event) => void changePack(event.target.value)}
            className="h-11 min-[761px]:h-9 rounded-md border border-input bg-background px-2 text-[16px] min-[761px]:text-sm"
          >
            <option value="">Kein Paket</option>
            {SECTOR_PACKS.map((pack) => (
              <option key={pack.id} value={pack.id}>
                {pack.label ?? pack.id}
              </option>
            ))}
          </select>
          {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
        </div>

        {activePack ? (
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>
              Approval-Pflicht:{' '}
              {activePack.requiredApprovals.length > 0
                ? activePack.requiredApprovals.join(', ')
                : 'keine'}
            </li>
            <li>
              Disclaimer aktiv für:{' '}
              {activePack.disclaimers.length > 0
                ? activePack.disclaimers.map((d) => d.outputType).join(', ')
                : 'keine'}
            </li>
            {activePack.providerRules.euOnly ? <li>Erzwingt EU-only-Provider.</li> : null}
          </ul>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {saved ? <p className="text-sm text-success" role="status">Gespeichert.</p> : null}
      </CardContent>
    </Card>
  )
}

function ChatModelsCard() {
  const { workspace } = useWorkspace()
  const [allowed, setAllowed] = useState<string[]>([])
  const [defaults, setDefaults] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [defaultSaving, setDefaultSaving] = useState(false)
  const [defaultError, setDefaultError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await apiRequest({
        path: '/admin/chat-models',
        schema: chatModelsResponseSchema,
      })
      setAllowed(data.models)
      setDefaults(data.defaults)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Modelle konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }

  const qc = useQueryClient()
  const save = async () => {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await apiRequest({
        path: '/admin/chat-models',
        method: 'PATCH',
        body: { models: allowed },
        schema: z.object({ models: z.array(z.string()) }),
      })
      await qc.invalidateQueries({ queryKey: ['workspace', 'current'] })
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Modelle konnten nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  const changeDefaultModel = async (model: string) => {
    setDefaultSaving(true)
    setDefaultError(null)
    try {
      await apiRequest({
        path: '/admin/default-model',
        method: 'PUT',
        body: { model },
        schema: defaultModelResponseSchema,
      })
      await qc.invalidateQueries({ queryKey: ['workspace', 'current'] })
    } catch (err) {
      setDefaultError(
        err instanceof Error ? err.message : 'Standard-Modell konnte nicht gespeichert werden.',
      )
    } finally {
      setDefaultSaving(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const toggle = (value: string) => {
    setAllowed((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value],
    )
    setSaved(false)
  }

  const resetToDefaults = () => {
    setAllowed(defaults)
    setSaved(false)
  }

  // Standard-Modell muss in der persistierten Allowlist liegen (serverseitig
  // geprueft) — daher gegen workspace.allowed_chat_models filtern, nicht
  // gegen den lokalen, ungespeicherten Entwurf.
  const defaultOptions = KNOWN_CHAT_MODELS.filter((m) =>
    workspace.allowed_chat_models.includes(m.value),
  )
  // Wurde das aktuelle Standard-Modell spaeter aus der Allowlist entfernt,
  // als deaktivierte Option sichtbar machen statt still eine falsche
  // Auswahl anzuzeigen.
  const currentDefaultMissing =
    workspace.default_model.length > 0 &&
    !defaultOptions.some((m) => m.value === workspace.default_model)

  return (
    <Card data-testid="chat-models-card">
      <CardHeader>
        <CardTitle>Chat-Modelle</CardTitle>
        <CardDescription>
          Erlaubte Modelle für diesen Workspace. Nicht freigegebene Modelle erscheinen nicht im
          Modell-Select und können serverseitig nicht gewählt werden.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="default-model" className="text-sm font-medium">
            Standard-Modell
          </label>
          <select
            id="default-model"
            value={workspace.default_model}
            disabled={defaultSaving || defaultOptions.length === 0}
            onChange={(event) => void changeDefaultModel(event.target.value)}
            className="h-11 min-[761px]:h-9 rounded-md border border-input bg-background px-2 text-[16px] min-[761px]:text-sm"
          >
            {defaultOptions.map((model) => (
              <option key={model.value} value={model.value}>
                {model.label}
              </option>
            ))}
            {currentDefaultMissing ? (
              <option value={workspace.default_model} disabled>
                {workspace.default_model} (nicht mehr freigegeben)
              </option>
            ) : null}
          </select>
          {defaultSaving ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
          <span className="text-xs text-muted-foreground">
            Wird für neue Chats und Anfragen ohne Modellwahl verwendet.
          </span>
        </div>
        {defaultError ? <p className="text-sm text-destructive">{defaultError}</p> : null}

        {loading ? (
          <p className="text-sm text-muted-foreground">Lädt…</p>
        ) : (
          <>
            <div className="space-y-2">
              {KNOWN_CHAT_MODELS.map((model) => (
                <label
                  key={model.value}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={allowed.includes(model.value)}
                    onChange={() => toggle(model.value)}
                  />
                  <span className="text-sm font-medium">{model.label}</span>
                  <code className="ml-auto text-xs text-muted-foreground">{model.value}</code>
                </label>
              ))}
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {saved ? <p className="text-sm text-success" role="status">Gespeichert.</p> : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={() => void save()} disabled={saving || allowed.length === 0}>
                {saving ? 'Speichert…' : 'Speichern'}
              </Button>
              <Button type="button" variant="outline" onClick={resetToDefaults} disabled={saving}>
                Auf Standard zurücksetzen
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
